import { useContext, useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Filter, X, ChevronDown, FileText, ArrowLeft, AlertCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import Pagination from '../../../../../../components/Pagination';
import auditQuestionService from '../../../../../../services/audit_services/auditQuestionService';

export default function AuditQuestionsList() {
  const { auditPlanId, sectionId } = useParams();
  const { hasAnyRole, authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [auditQuestions, setAuditQuestions] = useState([]);
  const [auditSection, setAuditSection] = useState(null);
  const [auditPlan, setAuditPlan] = useState(null);
  const [userRoleInPlan, setUserRoleInPlan] = useState(null); // User's role in the audit plan
  const [auditPlanStatus, setAuditPlanStatus] = useState(null); // Audit plan status
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    auditee_confirmation: '',
    ordering: 'created_at_desc'
  });

  // Separate state for applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    status: '',
    auditee_confirmation: '',
    ordering: 'created_at_desc'
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [showFiltersSection, setShowFiltersSection] = useState(false);

  // Can edit if:
  // 1. User has AuditUser or AuditManager role
  // 2. User is an auditor in the audit plan
  // 3. Audit plan status is 'in_progress'
  const canEdit = hasAnyRole(['AuditUser','AuditManager']) && 
                  userRoleInPlan === 'auditor' && 
                  auditPlanStatus === 'in_progress';

  useEffect(() => {
    fetchAuditQuestions();
  }, [auditPlanId, sectionId, pagination.page, pagination.page_size, appliedFilters]);

  const fetchAuditQuestions = async () => {
    setLoading(true);
    
    try {
      const filterParams = {
        search: appliedFilters.search,
        status: appliedFilters.status,
        auditee_confirmation: appliedFilters.auditee_confirmation,
        ordering: appliedFilters.ordering
      };

      const result = await auditQuestionService.getAuditQuestions(
        authTokens.access,
        auditPlanId,
        sectionId,
        filterParams,
        { page: pagination.page, page_size: pagination.page_size }
      );

      if (result.success) {
        setAuditQuestions(result.data.results || []);
        setPagination(prev => ({
          ...prev,
          count: result.data.count || 0,
          total_pages: result.data.total_pages || 1
        }));
        
        // Set audit section, plan, and user role from response
        if (result.data.audit_section) {
          setAuditSection(result.data.audit_section);
        }
        if (result.data.audit_plan) {
          setAuditPlan(result.data.audit_plan);
          setAuditPlanStatus(result.data.audit_plan.status);
        }
        if (result.data.my_role_in_audit_plan) {
          setUserRoleInPlan(result.data.my_role_in_audit_plan);
        }
      } else {
        showError(result.error || 'Error loading audit questions');
        setAuditQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching audit questions:', error);
      showError('Error loading audit questions');
      setAuditQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    const defaultFilters = {
      search: '',
      status: '',
      auditee_confirmation: '',
      ordering: 'created_at_desc'
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
      on_hold: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En pause' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getConfirmationBadge = (confirmation) => {
    const confirmationConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmé' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeté' },
      needs_clarification: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Nécessite une clarification' }
    };

    const config = confirmationConfig[confirmation] || confirmationConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des questions d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Questions d'audit</h1>
            <p className="text-sm text-gray-600 mt-1">
              {auditSection ? `Questions pour: ${auditSection.section_name}` : 'Chargement...'}
            </p>
          </div>
        </div>
        {canEdit && (
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/create`}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter une question
          </Link>
        )}
      </div>

      {/* Status Message - Show when user is auditor but can't edit due to status */}
      {hasAnyRole(['AuditUser','AuditManager']) && userRoleInPlan === 'auditor' && auditPlanStatus !== 'in_progress' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900">Modification restreinte</p>
              <p className="text-sm text-yellow-800 mt-1">
                Les questions ne peuvent être créées ou modifiées que lorsque le statut du plan d'audit est "En cours". 
                Statut actuel: <span className="font-semibold">{auditPlanStatus?.replace('_', ' ').toUpperCase()}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom de question, localisation ou description..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFiltersSection(!showFiltersSection)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters Section */}
        {showFiltersSection && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="in_progress">En cours</option>
                  <option value="completed">Terminé</option>
                  <option value="on_hold">En pause</option>
                </select>
              </div>

              {/* Auditee Confirmation Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmation de l'audité</label>
                <select
                  value={filters.auditee_confirmation}
                  onChange={(e) => handleFilterChange('auditee_confirmation', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Toutes les confirmations</option>
                  <option value="pending">En attente</option>
                  <option value="confirmed">Confirmé</option>
                  <option value="rejected">Rejeté</option>
                  <option value="needs_clarification">Nécessite une clarification</option>
                </select>
              </div>

              {/* Sorting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trier par</label>
                <select
                  value={filters.ordering}
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="created_at_desc">Plus récent d'abord</option>
                  <option value="created_at_asc">Plus ancien d'abord</option>
                  <option value="question_name_asc">Nom (A-Z)</option>
                  <option value="question_name_desc">Nom (Z-A)</option>
                  <option value="status_asc">Statut (A-Z)</option>
                  <option value="status_desc">Statut (Z-A)</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center justify-end space-x-3 mt-4">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Effacer les filtres
              </button>
              <button
                onClick={applyFilters}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Filter className="h-4 w-4 mr-2" />
                Appliquer les filtres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Questions Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {auditQuestions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune question d'audit trouvée</p>
            {canEdit && (
              <Link
                to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/create`}
                className="inline-flex items-center mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer votre première question
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Localisation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confirmation de l'audité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pièces jointes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditQuestions.map((question) => (
                  <tr key={question.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{question.question_name}</p>
                        <p className="text-xs text-gray-500">Créé par {question.created_by_name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">{question.location}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(question.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getConfirmationBadge(question.auditee_response_confirmation)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-900">{question.attachments_count || 0}</div>
                        <div className="text-xs text-gray-500">Fichiers</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${question.id}`}
                          className="text-green-600 hover:text-green-900"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {canEdit && (
                          <>
                            <Link
                              to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${question.id}/edit`}
                              className="text-orange-600 hover:text-orange-900"
                              title="Modifier"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {auditQuestions.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          pageSize={pagination.page_size}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          totalItems={pagination.count}
        />
      )}
    </div>
  );
}